document.addEventListener('DOMContentLoaded', function() {
  const STOREFRONT_TOKEN = document.querySelector('meta[name="storefront-api-token"]')?.content;
  const API_VERSION = '2024-01';
  const API_BASE = `/api/${API_VERSION}`;
  const PAGE_SIZE = 250; // Storefront API hard cap per page

  const manufacturerSelect = document.getElementById('manufacturer-select');
  const modelSelect = document.getElementById('model-select');
  const resultsContainer = document.getElementById('finder-results');

  let manufacturersData = [];
  let fitmentData = [];            // fitments for the currently selected manufacturer
  let allFitmentData = [];         // full, paginated fitment set (cached after first load)
  let fitmentLoadPromise = null;   // de-dupes concurrent loads

  // Small helper to run a Storefront GraphQL query
  async function storefrontQuery(query, variables = {}) {
    const response = await fetch(`${API_BASE}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query, variables })
    });
    return response.json();
  }

  // Fetch ALL fitment data, paginating through every page so we never truncate
  // at the 250-per-page Storefront cap. Cached after the first successful load.
  async function loadAllFitmentData() {
    if (allFitmentData.length > 0) return allFitmentData;
    if (fitmentLoadPromise) return fitmentLoadPromise;

    fitmentLoadPromise = (async () => {
      const collected = [];
      let cursor = null;
      let hasNextPage = true;
      let pageGuard = 0; // safety cap: 20 pages = up to 5000 fitments

      const query = `
        query FitmentPage($cursor: String) {
          metaobjects(type: "equipment_fitment", first: ${PAGE_SIZE}, after: $cursor, sortKey: "id") {
            edges {
              node {
                id
                fields {
                  key
                  value
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;

      while (hasNextPage && pageGuard < 20) {
        pageGuard++;
        const data = await storefrontQuery(query, { cursor });

        if (data.errors) {
          console.error('GraphQL errors loading fitments:', data.errors);
          break;
        }

        const connection = data.data?.metaobjects;
        if (!connection) break;

        connection.edges.forEach(edge => {
          const fields = {};
          edge.node.fields.forEach(field => {
            fields[field.key] = field.value;
          });

          // Parse fits_variants (stored as a JSON array of variant GIDs)
          let variants = [];
          const fitsVariantsRaw = fields.fits_variants;
          if (fitsVariantsRaw) {
            try {
              const parsed = JSON.parse(fitsVariantsRaw);
              variants = Array.isArray(parsed) ? parsed : [fitsVariantsRaw];
            } catch (e) {
              variants = [fitsVariantsRaw];
            }
          }

          collected.push({
            id: edge.node.id,
            manufacturer: fields.manufacturer,
            model: fields.model,
            fits_variants: variants
          });
        });

        hasNextPage = connection.pageInfo.hasNextPage;
        cursor = connection.pageInfo.endCursor;
      }

      allFitmentData = collected;
      console.log(`Loaded ${allFitmentData.length} fitment entries across ${pageGuard} page(s).`);
      return allFitmentData;
    })();

    try {
      return await fitmentLoadPromise;
    } finally {
      // Keep the cache; allow a fresh attempt only if nothing loaded.
      if (allFitmentData.length === 0) fitmentLoadPromise = null;
    }
  }

  // Fetch manufacturers on page load (independent of fitment data so the
  // dropdown appears immediately). Manufacturers comfortably fit under 250.
  async function loadManufacturers() {
    try {
      const query = `
        query {
          metaobjects(type: "manufacturer", first: ${PAGE_SIZE}) {
            edges {
              node {
                id
                handle
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      `;

      const data = await storefrontQuery(query);

      if (data.errors) {
        console.error('GraphQL errors loading manufacturers:', data.errors);
        return;
      }

      manufacturersData = data.data.metaobjects.edges
        .map(edge => {
          const fields = {};
          edge.node.fields.forEach(field => {
            fields[field.key] = field.value;
          });
          return {
            id: edge.node.id,
            handle: edge.node.handle,
            name: fields.name || edge.node.handle
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      // Populate manufacturer dropdown
      manufacturersData.forEach(manufacturer => {
        const option = document.createElement('option');
        option.value = manufacturer.id;
        option.textContent = manufacturer.name;
        manufacturerSelect.appendChild(option);
      });
    } catch (error) {
      console.error('Error loading manufacturers:', error);
    }
  }

  // When a manufacturer is selected, get a fresh model list for just that
  // manufacturer. Ensures the full fitment set is loaded (paginated/cached),
  // then filters client-side.
  async function loadFitmentData(manufacturerId) {
    modelSelect.innerHTML = '<option value="">Loading models…</option>';
    modelSelect.disabled = true;

    await loadAllFitmentData();

    // Guard against a newer selection having superseded this one
    if (manufacturerSelect.value !== manufacturerId) return;

    fitmentData = allFitmentData.filter(f => f.manufacturer === manufacturerId);

    if (fitmentData.length === 0) {
      console.warn('No fitment data found for manufacturer ID:', manufacturerId);
      modelSelect.innerHTML = '<option value="">No models available</option>';
      modelSelect.disabled = true;
      return;
    }

    // Populate model dropdown with unique models for this manufacturer
    const uniqueModels = [...new Set(fitmentData.map(f => f.model))].sort();
    modelSelect.innerHTML = '<option value="">-- Choose a model --</option>';
    uniqueModels.forEach(model => {
      const option = document.createElement('option');
      option.value = model;
      option.textContent = model;
      modelSelect.appendChild(option);
    });

    modelSelect.disabled = false;
  }

  // Fetch product details for selected model
  async function loadProductsForModel(model) {
    try {
      const modelFitments = fitmentData.filter(f => f.model === model);

      if (modelFitments.length === 0) {
        resultsContainer.innerHTML = '<div class="equipment-finder__no-results">No products found for this model.</div>';
        resultsContainer.style.display = 'block';
        return;
      }

      // Collect all variant GIDs from this model's fitments
      const variantSkus = [];
      modelFitments.forEach(fitment => {
        if (fitment.fits_variants) {
          variantSkus.push(...fitment.fits_variants);
        }
      });

      if (variantSkus.length === 0) {
        resultsContainer.innerHTML = '<div class="equipment-finder__no-results">No products found for this model.</div>';
        resultsContainer.style.display = 'block';
        return;
      }

      const uniqueVariantIds = [...new Set(variantSkus)];

      const query = `
        query {
          nodes(ids: [${uniqueVariantIds.map(id => `"${id}"`).join(', ')}]) {
            ... on ProductVariant {
              id
              title
              sku
              product {
                id
                title
                handle
                featuredImage {
                  url
                }
              }
            }
          }
        }
      `;

      const data = await storefrontQuery(query);

      if (data.errors) {
        console.error('GraphQL errors:', JSON.stringify(data.errors, null, 2));
        resultsContainer.innerHTML = '<div class="equipment-finder__no-results">Error loading products.</div>';
        resultsContainer.style.display = 'block';
        return;
      }

      // Map products with their variants
      const productVariantsMap = new Map();

      (data.data.nodes || [])
        .filter(node => node && node.product)
        .forEach(variant => {
          const product = variant.product;
          if (!productVariantsMap.has(product.id)) {
            productVariantsMap.set(product.id, {
              ...product,
              variants: []
            });
          }
          productVariantsMap.get(product.id).variants.push({
            id: variant.id,
            title: variant.title,
            sku: variant.sku
          });
        });

      if (productVariantsMap.size === 0) {
        resultsContainer.innerHTML = '<div class="equipment-finder__no-results">No products found for this model.</div>';
        resultsContainer.style.display = 'block';
        return;
      }

      // Render product cards
      let html = '<h3 class="equipment-finder__results-heading">Compatible Products</h3>';
      html += '<div class="equipment-finder__products">';

      productVariantsMap.forEach(product => {
        const imageUrl = product.featuredImage?.url || '/cdn/shop/products/placeholder.jpg';

        // Group variants by title for display
        const uniqueVariants = [...new Set(product.variants.map(v => v.title))];

        html += `
          <div class="equipment-finder__product-card">
            <img src="${imageUrl}" alt="${product.title}" class="equipment-finder__product-image">
            <div class="equipment-finder__product-content">
              <h4 class="equipment-finder__product-name">${product.title}</h4>
        `;

        if (uniqueVariants.length > 0) {
          html += `<div class="equipment-finder__product-variants">`;
          uniqueVariants.forEach(variantTitle => {
            const variantObj = product.variants.find(v => v.title === variantTitle);
            const variantId = variantObj?.id;

            // Deep-link with numeric variant ID extracted from the GID
            const numericVariantId = variantId.split('/').pop();
            const deepLinkUrl = `/products/${product.handle}?variant=${numericVariantId}`;

            html += `
              <div class="equipment-finder__product-variant">
                <span class="equipment-finder__product-variant-name">${variantTitle}</span>
                <a href="${deepLinkUrl}" class="equipment-finder__product-link">View</a>
              </div>
            `;
          });
          html += `</div>`;
        } else {
          const productUrl = `/products/${product.handle}`;
          html += `<a href="${productUrl}" class="equipment-finder__product-link">View Product</a>`;
        }

        html += `
            </div>
          </div>
        `;
      });

      html += '</div>';
      resultsContainer.innerHTML = html;
      resultsContainer.style.display = 'block';

      resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      console.error('Error loading products:', error);
      resultsContainer.innerHTML = '<div class="equipment-finder__no-results">Error loading products.</div>';
      resultsContainer.style.display = 'block';
    }
  }

  // Update URL when selections change
  function updateURLState() {
    const manufacturerId = manufacturerSelect.value;
    const modelName = modelSelect.value;

    const params = new URLSearchParams();
    if (manufacturerId) params.set('manufacturer', manufacturerId);
    if (modelName) params.set('model', modelName);

    const newURL = params.toString() ? `?${params.toString()}` : window.location.pathname;
    window.history.pushState({ manufacturerId, modelName }, '', newURL);
  }

  // Restore selections from URL
  async function restoreFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const manufacturerId = urlParams.get('manufacturer');
    const modelName = urlParams.get('model');

    if (manufacturerId && manufacturersData.length > 0) {
      manufacturerSelect.value = manufacturerId;
      await loadFitmentData(manufacturerId);

      if (modelName) {
        modelSelect.value = modelName;
        if (modelSelect.value === modelName) {
          loadProductsForModel(modelName);
        }
      }
    }
  }

  // Event listeners
  manufacturerSelect.addEventListener('change', function() {
    modelSelect.innerHTML = '<option value="">-- Choose a model --</option>';
    modelSelect.disabled = true;
    resultsContainer.style.display = 'none';

    if (this.value) {
      loadFitmentData(this.value);
      updateURLState();
    }
  });

  modelSelect.addEventListener('change', function() {
    resultsContainer.style.display = 'none';

    if (this.value) {
      loadProductsForModel(this.value);
      updateURLState();
    }
  });

  // Load manufacturers on page load, then restore any URL state
  loadManufacturers().then(restoreFromURL);
});
